import { useState } from "react";
import { Copy, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui";
import { PopButton } from "../PopButton";
import { standingsCardUrl, standingsShareUrl, type LmsComp } from "../../lib/lms";

export function ShareStandings({ comp }: { comp: LmsComp }) {
  const [open, setOpen] = useState(false);
  const alive = comp.players.filter((p) => p.alive).length;
  const gw = comp.history.length;
  const url = standingsShareUrl(comp.id);
  const text = comp.winner_id ? `🏆 We have a winner in ${comp.name}!` : `⚽ ${comp.name}: ${alive} still in${gw ? ` after GW ${gw}` : ""}.`;
  const card = standingsCardUrl(comp.id, `${gw}-${alive}-${comp.players.length}`);
  const share = async () => {
    try {
      const blob = await (await fetch(card)).blob();
      const file = new File([blob], "still-in.png", { type: "image/png" });
      const nav = navigator as any;
      if (nav.canShare?.({ files: [file] })) return await nav.share({ files: [file], title: comp.name, text: `${text} ${url}` });
      if (nav.share) return await nav.share({ title: comp.name, text, url });
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied — paste it in the group chat");
    } catch { /* share cancelled */ }
  };
  const copy = async () => { await navigator.clipboard.writeText(url).catch(() => undefined); toast.success("Link copied"); };
  return (
    <>
      <PopButton size="sm" tone="yellow" onClick={() => setOpen(true)} data-testid="lms-share-standings-button"><Share2 className="h-4 w-4" /> Share standings</PopButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl border-[3px] border-[#0B1F33] bg-[#FFF5E1]" data-testid="lms-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-comic text-2xl">Who's still in?</DialogTitle>
            <DialogDescription>Send the latest standings to the group chat.</DialogDescription>
          </DialogHeader>
          <img src={card} alt={`${comp.name} standings card`} className="w-full rounded-xl border-[3px] border-[#0B1F33]" data-testid="lms-share-card-image" />
          <div className="grid grid-cols-3 gap-2">
            <PopButton size="sm" tone="green" onClick={share} data-testid="lms-share-native"><Share2 className="h-4 w-4" /> Share</PopButton>
            <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noreferrer" className="btn-pop inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-3 py-2 font-comic text-sm uppercase text-white" data-testid="lms-share-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <PopButton size="sm" tone="white" onClick={copy} data-testid="lms-share-copy"><Copy className="h-4 w-4" /> Copy link</PopButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
